import React from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import useUserAuth from '../../../../utils/useUserAuth';
import { formatSportTypes } from './sportTypeLabel';
import AccessDoorRow from './AccessDoorRow';

import {
  TicketCard,
  ExpiredCardWrapper,
  CardInfoSection,
  HeaderRow,
  HeaderLeft,
  ClassTypeLabel,
  TicketTitle,
  TypeLabel,
  InfoRow,
  InfoLeft,
  InfoLabel,
  InfoValue,
  CountGroup,
  CountCurrent,
  CountTotal,
  HighlightText,
  ExpiredLabel,
  UpcomingLabel,
} from './CourseTicketCardStyled';
import { usageBranchLabel, canEditUseBranches } from '../../../../utils/branchShare';
import { toActionRows } from './cardActions';
import { CardButtonArea, CardButton } from './PassCardStyled';

const URGENT_DAYS_THRESHOLD = 10;

const TrialTicketCard = ({ trial, isExpired, onPeriodChange, onBatchReservation, onInstructorChange, onHistoryOpen, onUseBranchEdit, onChangeHistoryOpen }) => {
  const canPeriodChange = useUserAuth('MEMBER_PASS_PERIOD_CHANGE');
  const canBatchReserve = useUserAuth('MEMBER_LESSON_BATCH_RESERVE');
  const isStoppedStatus = trial.status === 'STOPPED';
  const isStopScheduled = isStoppedStatus && trial.stoppedDate && dayjs(trial.stoppedDate).startOf('day').isAfter(dayjs().startOf('day'));
  const isStopped = isStoppedStatus && !isStopScheduled;

  // 잔여 정보 계산.
  const totalCount = Number(trial.usageCount) || 0;
  const usedCount = Number(trial.usedCount) || 0;
  const remainingCount =
    typeof trial.remainingCount === 'number' ? trial.remainingCount : Math.max(totalCount - usedCount, 0);
  // 차감은 출결 확정 시점이라 remainingCount에는 예약중 건수가 빠져 있다 —
  // reservedCount(백엔드 배치 집계)를 함께 보여줘 초과 예약 오인을 막는다.
  const reservedCount = Number(trial.reservedCount) || 0;

  // 시작일이 미래면 '사용예정'으로 표시하고 잔여일수는 계산하지 않는다.
  const today = dayjs().startOf('day');
  const isUpcoming = trial.issueDate ? today.isBefore(dayjs(trial.issueDate).startOf('day')) : false;

  const daysRemaining =
    !isUpcoming && trial.expireDate ? dayjs(trial.expireDate).startOf('day').diff(today, 'day') : null;
  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= URGENT_DAYS_THRESHOLD;

  // classType=PASS(이용권) 일일권은 레슨 예약 개념이 없으므로 일괄예약 버튼을 숨긴다.
  const showBatchReservation = !isExpired && trial.classType !== 'PASS';
  // 담당강사는 개인레슨 일일권만 — 그룹레슨은 수업별 강사, 이용권은 강사 개념 자체가 없다.
  const isPrivateLesson = trial.classType === 'PRIVATE_LESSON';
  const showInstructorChange = !isExpired && isPrivateLesson && !!onInstructorChange;

  const sportTypeText = formatSportTypes(trial.sportTypes);

  const usageLabel = usageBranchLabel(trial.branchShare, { forMember: true });
  const showUseBranchEdit = !isExpired && !!onUseBranchEdit && canEditUseBranches(trial.branchShare);

  // 하단 액션 버튼 — 조건에 맞는 것만 모아 2열로 배치한다(홀수면 마지막이 전폭).
  const actions = [
    {
      key: 'period',
      label: '상품 정보 수정',
      onClick: canPeriodChange ? onPeriodChange : undefined,
      tooltip: !canPeriodChange ? '권한이 없습니다' : '',
      dimmed: !canPeriodChange,
    },
    ...(showInstructorChange ? [{
      key: 'instructor',
      label: '담당 강사 변경',
      onClick: onInstructorChange,
    }] : []),
    ...(showBatchReservation ? [{
      key: 'batchReserve',
      label: '레슨 일괄예약',
      onClick: canBatchReserve ? onBatchReservation : undefined,
      tooltip: !canBatchReserve ? '권한이 없습니다' : '',
      dimmed: !canBatchReserve,
    }] : []),
    ...(showUseBranchEdit ? [{
      key: 'useBranch',
      label: '이용지점 변경',
      onClick: onUseBranchEdit,
    }] : []),
    ...(onChangeHistoryOpen ? [{
      key: 'changeHistory',
      label: '변경 내역 조회',
      onClick: onChangeHistoryOpen,
    }] : []),
  ];
  const actionRows = toActionRows(actions);

  const card = (
    <TicketCard isStopped={!isExpired && isStopped}>
      <CardInfoSection>
        {/* Row 1: 지점 + 상품명 | 중지 상태 */}
        <HeaderRow>
          <HeaderLeft>
            <ClassTypeLabel isStopped={!isExpired && isStopped}>{trial.branchName}</ClassTypeLabel>
            <Tooltip title={trial.trialName} arrow componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
              <TicketTitle isStopped={!isExpired && isStopped}>{trial.trialName}</TicketTitle>
            </Tooltip>
          </HeaderLeft>
          {/* 상품종류(일일권) 라벨은 섹션 제목과 중복이라 제거하고, 중지 상태만 남긴다. */}
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {!isExpired && (isStopScheduled || isStopped) && (
              <TypeLabel isStopped={isStopped} sx={isStopScheduled ? { color: '#EE735A' } : {}}>
                {isStopScheduled ? `중지 예정 (${dayjs(trial.stoppedDate).format('M/D')}~)` : '중지'}
              </TypeLabel>
            )}
            {onHistoryOpen && trial.classType !== 'PASS' && (
              <Box
                component="button"
                type="button"
                onClick={onHistoryOpen}
                sx={{
                  border: '1px solid #E0E4EA',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  color: '#5C6470',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  padding: '0 9px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 150ms ease',
                  '&:hover': { backgroundColor: '#F6F9FE', borderColor: '#B9CBF2', color: '#1976d2' },
                }}>
                예약 내역
              </Box>
            )}
          </Stack>
        </HeaderRow>

        {/* Row 1.5: 다지점 공유 상품 이용지점(#98) — 비공유면 빈 문자열이라 안 그린다 */}
        {usageLabel && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>이용지점</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>{usageLabel}</InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 2: 운동종목 (다종목 상품은 쉼표로 나열) */}
        {sportTypeText && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>종목</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>{sportTypeText}</InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 2.5: 담당강사 (개인레슨만 — 배정은 판매등록/담당강사 변경으로 저장된다) */}
        {isPrivateLesson && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>담당강사</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>{trial.trainerName || '-'}</InfoValue>
            </InfoLeft>
          </InfoRow>
        )}
        {/* 출입문 (전환 지점만 렌더 — #91 회원별 오버라이드) */}
        <AccessDoorRow
          holdingType="TRIAL"
          holdingId={trial.memberTrialTicketId}
          isStopped={!isExpired && isStopped}
        />

        {/* Row 3: 잔여횟수 (횟수가 있고 · 횟수제일 때만; 기간제 PERIOD 는 숨김) */}
        {totalCount > 0 && trial.usageType !== 'PERIOD' && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>잔여횟수</InfoLabel>
              <CountGroup>
                <CountCurrent isStopped={!isExpired && isStopped}>{remainingCount}회</CountCurrent>
                <CountTotal isStopped={!isExpired && isStopped}>/</CountTotal>
                <CountTotal isStopped={!isExpired && isStopped}>{totalCount}회</CountTotal>
              </CountGroup>
            </InfoLeft>
            {!isExpired && <HighlightText isStopped={isStopped}>{remainingCount}회 남음</HighlightText>}
          </InfoRow>
        )}

        {/* Row 3.5: 차감 예정(예약) */}
        {totalCount > 0 && trial.usageType !== 'PERIOD' && !isExpired && reservedCount > 0 && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={isStopped} sx={{ whiteSpace: 'nowrap' }}>예약(차감 예정 횟수)</InfoLabel>
            </InfoLeft>
            <HighlightText isStopped={isStopped}>{reservedCount}회 예정</HighlightText>
          </InfoRow>
        )}

        {/* Row 4: 유효기간 + 잔여일수 */}
        <InfoRow>
          <InfoLeft>
            <InfoLabel isStopped={!isExpired && isStopped}>유효기간</InfoLabel>
            <InfoValue isStopped={!isExpired && isStopped}>
              {trial.issueDate?.replaceAll('-', '.')} ~ {trial.expireDate?.replaceAll('-', '.')}
            </InfoValue>
          </InfoLeft>
          {isExpired ? (
            <ExpiredLabel>만료</ExpiredLabel>
          ) : isUpcoming ? (
            <UpcomingLabel>사용예정</UpcomingLabel>
          ) : (
            daysRemaining !== null &&
            daysRemaining >= 0 && (
              <HighlightText isStopped={isStopped} urgent={isUrgent}>
                {daysRemaining}일 남음
              </HighlightText>
            )
          )}
        </InfoRow>
      </CardInfoSection>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {actionRows.map((row, rowIndex) => (
          <CardButtonArea key={row.map(action => action.key).join('-')} isStopped={!isExpired && isStopped}>
            {row.map((action, index) => (
              <Tooltip key={action.key} title={action.tooltip ?? ''} arrow>
                <CardButton
                  variant="text"
                  isStopped={!isExpired && isStopped}
                  noBorderTop={rowIndex > 0}
                  noBorderRight={row.length === 2 && index === 0}
                  disabled={action.disabled}
                  onClick={action.onClick}
                  style={action.dimmed ? { opacity: 0.5, cursor: 'default' } : {}}>
                  {action.label}
                </CardButton>
              </Tooltip>
            ))}
          </CardButtonArea>
        ))}
      </Box>
    </TicketCard>
  );

  if (isExpired) {
    return <ExpiredCardWrapper>{card}</ExpiredCardWrapper>;
  }

  return <Box>{card}</Box>;
};

export default TrialTicketCard;
